import { useState } from 'react';

import { ProductFilterCategory } from ':cloud/content/products';
import { useAllAvailableProducts } from ':cloud/hooks/useAllAvailableProducts';

type DoesIntersectionExistProps = { productCategories: string[]; selectedFiltersArr: string[] };
// Helper functions to calculate new selected filters
function doesIntersectionExist({
  productCategories,
  selectedFiltersArr,
}: DoesIntersectionExistProps) {
  return productCategories.some((value) => selectedFiltersArr.includes(value));
}
const initialFilterState = new Set<ProductFilterCategory>([ProductFilterCategory.ALL]);

function useAllProductFilter() {
  const [selectedFilters, setSelectedFilters] = useState(initialFilterState);

  const availableProducts = useAllAvailableProducts();
  const [productItems, setProductItems] = useState(availableProducts);

  const addSelectedFilter = (filter) => {
    if (filter === ProductFilterCategory.ALL) {
      setSelectedFilters(initialFilterState);
      return initialFilterState;
    }
    const updatedFilterSet = new Set<ProductFilterCategory>(selectedFilters);
    updatedFilterSet.add(filter);
    // if ALL is already selected, and selection changes to a specific filter, remove ALL
    if (updatedFilterSet.size > 1 && updatedFilterSet.has(ProductFilterCategory.ALL)) {
      updatedFilterSet.delete(ProductFilterCategory.ALL);
    }

    // if all filters are selected, change selection to ALL
    if (updatedFilterSet.size === Object.keys(ProductFilterCategory).length - 1) {
      updatedFilterSet.clear();
      updatedFilterSet.add(ProductFilterCategory.ALL);
    }
    setSelectedFilters(updatedFilterSet);
    return updatedFilterSet;
  };
  const removeSelectedFilter = (filter: ProductFilterCategory) => {
    const updatedFilterSet = new Set<ProductFilterCategory>(selectedFilters);
    updatedFilterSet.delete(filter);
    if (updatedFilterSet.size === 0) {
      updatedFilterSet.add(ProductFilterCategory.ALL);
    }
    setSelectedFilters(updatedFilterSet);
    return updatedFilterSet;
  };

  const handleFilterChange = (filter: ProductFilterCategory) => {
    const isEnablingFilter = !selectedFilters.has(filter);
    let newFilterSet = new Set<ProductFilterCategory>();

    if (isEnablingFilter) {
      newFilterSet = addSelectedFilter(filter);
    } else {
      newFilterSet = removeSelectedFilter(filter);
    }

    if (newFilterSet.has(ProductFilterCategory.ALL)) {
      setProductItems(availableProducts);
      return;
    }

    setProductItems(
      availableProducts.filter((product) => {
        return doesIntersectionExist({
          productCategories: product.categories,
          selectedFiltersArr: Array.from(newFilterSet),
        });
      }),
    );
  };

  return {
    handleFilterChange,
    productItems,
    selectedFilters,
  };
}
export default useAllProductFilter;
